import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Lock, LogOut, ImagePlus, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { LOGO_URL } from '../data/content';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

const inputCls =
  'w-full bg-[#060B19] border border-[#1E293B] focus:border-[#FF5C00] focus:outline-none rounded-xl px-4 py-3.5 text-sm text-[#F2F4F8] placeholder:text-[#A0ABC0]/60 transition-colors duration-300';

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Giriş başarısız. Bilgilerinizi kontrol edin.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md bg-[#0A1128] border border-[#1E293B] rounded-2xl p-10"
      >
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="Vira Nakliyat" className="h-12 w-auto" />
        </div>
        <h1 className="font-display text-2xl tracking-tight text-center mb-2">Yönetim Paneli</h1>
        <p className="text-[#A0ABC0] text-sm text-center mb-8">Galeri fotoğraflarını yönetmek için giriş yapın</p>

        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-4">
          <input
            data-testid="admin-email-input"
            type="email"
            className={inputCls}
            placeholder="E-posta"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="relative">
            <input
              data-testid="admin-password-input"
              type={show ? 'text' : 'password'}
              className={`${inputCls} pr-12`}
              placeholder="Şifre"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              data-testid="admin-password-toggle"
              onClick={() => setShow(!show)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0ABC0] hover:text-[#F2F4F8]"
            >
              {show ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#FF5C00]">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2.5 bg-[#FF5C00] hover:bg-[#E65000] disabled:opacity-60 text-[#0A1128] font-bold rounded-full py-3.5 transition-colors duration-300"
          >
            <Lock size={16} />
            {busy ? 'Giriş yapılıyor...' : 'Giriş Yap'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const PhotoManager = ({ user, onLogout }) => {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get('/gallery');
    setPhotos(data);
  };

  useEffect(() => {
    load().catch(() => setError('Fotoğraflar yüklenemedi.'));
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (!file) {
      setError('Lütfen bir fotoğraf seçin.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title);
      fd.append('tag', tag);
      await api.post('/gallery/upload', fd);
      setNotice('Fotoğraf yüklendi ve galeriye eklendi.');
      setFile(null);
      setTitle('');
      setTag('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Yükleme başarısız oldu.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setError('');
    try {
      await api.delete(`/gallery/${id}`);
      await load();
    } catch {
      setError('Silme işlemi başarısız oldu.');
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF5C00] mb-2">Yönetim Paneli</p>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight">Galeri Fotoğrafları</h1>
            <p className="text-[#A0ABC0] text-sm mt-2">
              Yüklediğiniz fotoğraflar ana sayfa galerisinde stok görsellerin yerini alır.
            </p>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 border border-[#1E293B] hover:border-[#FF5C00] text-[#F2F4F8] rounded-full px-5 py-2.5 text-sm transition-colors duration-300"
          >
            <LogOut size={15} /> Çıkış
          </button>
        </div>

        <form
          onSubmit={upload}
          data-testid="gallery-upload-form"
          className="bg-[#0A1128] border border-[#1E293B] rounded-2xl p-7 md:p-9 mb-10 grid md:grid-cols-4 gap-4 items-end"
        >
          <div className="md:col-span-2">
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#A0ABC0] mb-2">Fotoğraf *</label>
            <input
              ref={fileRef}
              data-testid="gallery-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-[#A0ABC0] file:mr-4 file:rounded-full file:border-0 file:bg-[#FF5C00] file:text-[#0A1128] file:font-bold file:px-5 file:py-2.5 file:cursor-pointer hover:file:bg-[#E65000]"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#A0ABC0] mb-2">Başlık</label>
            <input
              data-testid="gallery-title-input"
              className={inputCls}
              placeholder="Örn: Asansörlü taşıma"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#A0ABC0] mb-2">Etiket</label>
            <input
              data-testid="gallery-tag-input"
              className={inputCls}
              placeholder="Örn: Evden Eve"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            />
          </div>
          <div className="md:col-span-4 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={busy}
              data-testid="gallery-upload-button"
              className="inline-flex items-center gap-2.5 bg-[#FF5C00] hover:bg-[#E65000] disabled:opacity-60 text-[#0A1128] font-bold rounded-full px-7 py-3.5 transition-colors duration-300"
            >
              <ImagePlus size={17} />
              {busy ? 'Yükleniyor...' : 'Galeriye Yükle'}
            </button>
            {notice && <p data-testid="gallery-upload-success" className="text-sm text-[#25D366]">{notice}</p>}
            {error && <p data-testid="gallery-error" className="text-sm text-[#FF5C00]">{error}</p>}
          </div>
        </form>

        {photos.length === 0 ? (
          <div
            data-testid="gallery-empty-state"
            className="border border-dashed border-[#1E293B] rounded-2xl p-14 text-center text-[#A0ABC0] text-sm"
          >
            Henüz fotoğraf yok. İlk taşıma fotoğrafınızı yükleyin — ana sayfadaki galeri otomatik güncellenir.
          </div>
        ) : (
          <div data-testid="gallery-photo-grid" className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`gallery-photo-item-${p.id}`}
                className="group relative rounded-xl overflow-hidden border border-[#1E293B] aspect-[4/3]"
              >
                <img
                  src={`${BACKEND_URL}/api/gallery/file/${p.storage_path}`}
                  alt={p.title || 'Galeri fotoğrafı'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060B19]/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{p.title || '—'}</p>
                    {p.tag && <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF5C00]">{p.tag}</p>}
                  </div>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`gallery-delete-button-${p.id}`}
                    className="shrink-0 w-9 h-9 rounded-full bg-[#FF5C00] text-[#0A1128] flex items-center justify-center hover:bg-[#E65000] transition-colors duration-300"
                    aria-label="Sil"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get('/auth/me')
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    setUser(false);
  };

  return (
    <main data-testid="admin-page">
      {user === null ? (
        <div className="min-h-screen flex items-center justify-center text-[#A0ABC0]">
          <RefreshCw size={22} className="animate-spin" />
        </div>
      ) : user === false ? (
        <LoginCard onLogin={setUser} />
      ) : (
        <PhotoManager user={user} onLogout={logout} />
      )}
    </main>
  );
}
